import axios from 'axios';

const API_BASE_URL = 'https://serenity.medista.ai/bessemer/api';

// Chat/Q&A Endpoints
export const sendChatMessage = async (message: string) => {
  const response = await axios.post(`${API_BASE_URL}/chat`, { message });
  return response.data;
};

export const clearChatHistory = async () => {
  const response = await axios.post(`${API_BASE_URL}/chat/clear`);
  return response.data;
};

// SFDC Data Endpoints
export const getSfdcContacts = async () => {
  const response = await axios.get(`${API_BASE_URL}/data/sfdc/contacts`);
  return response.data;
};

export const getSfdcAccounts = async () => {
  const response = await axios.get(`${API_BASE_URL}/data/sfdc/accounts`);
  return response.data;
};

export const getSfdcOpportunities = async () => {
  const response = await axios.get(`${API_BASE_URL}/data/sfdc/opportunities`);
  return response.data;
};

// Affinity Data Endpoints
export const getAffinityContacts = async () => {
  const response = await axios.get(`${API_BASE_URL}/data/affinity/contacts`);
  return response.data;
};

export const getAffinityCompanies = async () => {
  const response = await axios.get(`${API_BASE_URL}/data/affinity/companies`);
  return response.data;
};

export const getAffinityInteractions = async () => {
  const response = await axios.get(`${API_BASE_URL}/data/affinity/interactions`);
  return response.data;
};

// Data Hygiene Endpoints
export const checkDataHygiene = async () => {
  const response = await axios.get(`${API_BASE_URL}/hygiene/check`);
  return response.data;
};

export const getHygieneIssues = async (source = 'all', type = 'all') => {
  try {
    const response = await axios.get(`${API_BASE_URL}/hygiene/issues?source=${source}&type=${type}`);
    return response.data;
  } catch (error) {
    // Fallback to mock data when backend is not available
    console.warn('Backend not available, using mock data for hygiene issues');
    return getMockHygieneIssues(source, type);
  }
};

// Mock data for when backend is not available
const getMockHygieneIssues = (source: string, type: string) => {
  const mockIssues = [
    {
      id: 1,
      source: "sfdc",
      entity: "contacts",
      record_id: "SFDC-C1000",
      field: "FirstName",
      value: "jennifer",
      issue: "Case inconsistency",
      recommendation: "Jennifer",
      related_id: ""
    },
    {
      id: 2,
      source: "sfdc",
      entity: "contacts",
      record_id: "SFDC-C1001",
      field: "LastName",
      value: "SMITH",
      issue: "Case inconsistency",
      recommendation: "Smith",
      related_id: ""
    },
    {
      id: 3,
      source: "sfdc",
      entity: "contacts",
      record_id: "SFDC-C1002",
      field: "Phone",
      value: "555-1234",
      issue: "Invalid phone format",
      recommendation: "+1 (555) 123-4567",
      related_id: ""
    },
    {
      id: 4,
      source: "affinity",
      entity: "contacts",
      record_id: "AFF-100",
      field: "Email",
      value: "john.doe@example.com",
      issue: "Email overlap with SFDC",
      recommendation: "Verify if this is the same person and consolidate records",
      related_id: "SFDC-C1005"
    },
    {
      id: 5,
      source: "sfdc",
      entity: "opportunities",
      record_id: "SFDC-O500",
      field: "CloseDate",
      value: "2025-12-31",
      issue: "Future date",
      recommendation: "Verify if this is intentional (forecast)",
      related_id: ""
    },
    {
      id: 6,
      source: "affinity",
      entity: "contacts",
      record_id: "AFF-101",
      field: "Name",
      value: "jane smith",
      issue: "Case inconsistency",
      recommendation: "Jane Smith",
      related_id: ""
    },
    {
      id: 7,
      source: "sfdc",
      entity: "contacts",
      record_id: "SFDC-C1003",
      field: "Email",
      value: "jane.smith@company.com",
      issue: "Email overlap with Affinity",
      recommendation: "Verify if this is the same person and consolidate records",
      related_id: "AFF-101"
    },
    {
      id: 8,
      source: "sfdc",
      entity: "accounts",
      record_id: "SFDC-A100",
      field: "AnnualRevenue",
      value: "-50000",
      issue: "Negative revenue",
      recommendation: "50000",
      related_id: ""
    },
    {
      id: 9,
      source: "affinity",
      entity: "companies",
      record_id: "AFF-C100",
      field: "EmployeeCount",
      value: "-10",
      issue: "Negative employee count",
      recommendation: "10",
      related_id: ""
    },
    {
      id: 10,
      source: "sfdc",
      entity: "contacts",
      record_id: "SFDC-C1004",
      field: "CreatedDate",
      value: "2025-12-25",
      issue: "Future date",
      recommendation: "2025-01-14",
      related_id: ""
    }
  ];

  // Filter by source if specified
  let filteredIssues = mockIssues;
  if (source !== 'all') {
    filteredIssues = mockIssues.filter(issue => issue.source === source);
  }

  // Filter by type if specified
  if (type !== 'all') {
    filteredIssues = filteredIssues.filter(issue => issue.issue.toLowerCase().includes(type.toLowerCase()));
  }

  return filteredIssues;
};

// Data Model Analysis Endpoints
export const analyzeDataModels = async () => {
  const response = await axios.get(`${API_BASE_URL}/model/analyze`);
  return response.data;
};

export const suggestDataModel = async () => {
  const response = await axios.get(`${API_BASE_URL}/model/suggest`);
  return response.data;
};

// Data Warehouse Endpoints
export const getWarehouseSchema = async () => {
  const response = await axios.get(`${API_BASE_URL}/data/warehouse/schema`);
  return response.data;
};

export const getWarehouseTables = async () => {
  const response = await axios.get(`${API_BASE_URL}/data/warehouse/tables`);
  return response.data;
};

export const getWarehouseTableData = async (tableName: string) => {
  const response = await axios.get(`${API_BASE_URL}/data/warehouse/table/${tableName}`);
  return response.data;
};

// Unified Data Endpoints
export const getUnifiedContacts = async () => {
  const response = await axios.get(`${API_BASE_URL}/data/warehouse/table/unified_contacts`);
  return response.data;
};

export const getUnifiedOpportunities = async () => {
  const response = await axios.get(`${API_BASE_URL}/data/warehouse/table/unified_opportunities`);
  return response.data;
};

export const getUnifiedInteractions = async () => {
  const response = await axios.get(`${API_BASE_URL}/data/warehouse/table/unified_interactions`);
  return response.data;
};

export const getUnifiedCompanies = async () => {
  const response = await axios.get(`${API_BASE_URL}/data/warehouse/table/unified_companies`);
  return response.data;
};

// Business Analytics Endpoints
export const getBusinessOverview = async () => {
  const response = await axios.get(`${API_BASE_URL}/analytics/business/overview`);
  return response.data;
};

export const getSalesAnalytics = async () => {
  const response = await axios.get(`${API_BASE_URL}/analytics/business/sales`);
  return response.data;
};

export const getEngagementAnalytics = async () => {
  const response = await axios.get(`${API_BASE_URL}/analytics/business/engagement`);
  return response.data;
};
